import { useMemo } from 'react';
import { cloneDeep } from 'lodash';
import type { WritableDeep } from 'type-fest';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id, SectionTypeId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import type { ReferencesContext_SectionType } from '../../../contexts/ReferencesContext';
import type { GenerateId } from '../../../utils/generate-id.util';

import { useDocumentEditorContext } from '../contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import type { Editable } from '../types/editable.type';
import type { PrintGroup } from '../types/print-group.type';
import type { EnrichedSection } from '../types/enriched-section.type';

import { useEmptyPrinterDoc } from './useEmptyPrinterDoc.hook';
import { useDisplayedVirtualRoot } from './useDisplayedVirtualRoot.hook';

interface Accumulator {
  readonly groups: readonly PrintGroup[];
  readonly addHeaders: boolean;
  readonly addFooters: boolean;
  readonly supportPageCount: boolean;
  readonly sectionTypesIdMap: Record<
    SectionTypeId,
    ReferencesContext_SectionType
  >;
  readonly generateId: GenerateId;
}

const groupConsistentContent = (
  previousValue: Readonly<Accumulator>,
  currentValue: Readonly<EnrichedSection>,
): Readonly<Accumulator> => {
  const groups: WritableDeep<PrintGroup>[] = cloneDeep(
    previousValue.groups,
  ) as WritableDeep<PrintGroup>[];

  const { sectionTypesIdMap, generateId } = previousValue;

  const settings = sectionTypesIdMap[currentValue.typeId] ?? {
    hideHeaders: false,
    hideFooters: false,
    supportPageCount: false,
  };

  const { hideHeaders, hideFooters, supportPageCount } = settings;

  // Handle hasPagebreakBefore (common for both virtual and non-virtual sections)
  if (currentValue.hasPagebreakBefore && groups.length) {
    const previousGroup = groups[groups.length - 1];
    if (previousGroup) {
      const lastSection =
        previousGroup.sections[previousGroup.sections.length - 1];

      if (lastSection) {
        lastSection.hasPagebreakAfter = true;
      }
    }
  }

  // Add non-virtual sections to print groups
  if (!currentValue.isVirtual) {
    const sectionToPrint: WritableDeep<Readonly<EnrichedSection & { id: Id }>> =
      {
        ...currentValue,
        id: currentValue._id,
      } as WritableDeep<Readonly<EnrichedSection & { id: Id }>>;

    const previousGroup = groups[groups.length - 1];

    if (
      !groups.length ||
      previousGroup?.orientation !== sectionToPrint.orientation ||
      previousGroup.paperFormat !== sectionToPrint.paperFormat ||
      previousGroup.addFooters !== !hideFooters ||
      previousGroup.addHeaders !== !hideHeaders ||
      previousGroup.supportPageCount !== supportPageCount
    ) {
      groups.push({
        index: groups.length,
        addFooters: !hideFooters,
        addHeaders: !hideHeaders,
        supportPageCount,
        orientation: sectionToPrint.orientation,
        paperFormat: sectionToPrint.paperFormat,
        sections: [sectionToPrint],
      });
    } else {
      const previousGroup = groups[groups.length - 1];

      if (previousGroup) {
        previousGroup.sections.push(sectionToPrint);
      }
    }
  }

  // Process subsections recursively (common for both virtual and non-virtual sections)
  const withSubSections = currentValue.subSections.reduce(
    groupConsistentContent,
    {
      groups,
      addHeaders: !hideHeaders,
      addFooters: !hideFooters,
      supportPageCount,
      sectionTypesIdMap,
      generateId,
    },
  );

  // Handle hasPagebreakAfter for virtual sections only (applied after subsections)
  if (
    currentValue.isVirtual &&
    currentValue.hasPagebreakAfter &&
    withSubSections.groups.length
  ) {
    const mutableGroups = withSubSections.groups as WritableDeep<PrintGroup>[];
    const lastGroup = mutableGroups[mutableGroups.length - 1];
    if (lastGroup) {
      const lastSection = lastGroup.sections[lastGroup.sections.length - 1];

      if (lastSection) {
        lastSection.hasPagebreakAfter = true;
      }
    }
  }

  return {
    groups: withSubSections.groups,
    addHeaders: previousValue.addHeaders,
    addFooters: previousValue.addFooters,
    supportPageCount: previousValue.supportPageCount,
    sectionTypesIdMap,
    generateId,
  };
};

export const usePrinterInput = () => {
  const {
    enrichedVirtualRoot,
    props: {
      getters: { printing: getPrinting },
    },
    state: { editable },
  } = useDocumentEditorContext<Editable>();

  const { sectionTypesIdMap } = useReferencesContext();
  const { generateId } = useIdFactory();
  const { emptyDoc } = useEmptyPrinterDoc();
  const displayedVirtualRoot = useDisplayedVirtualRoot(enrichedVirtualRoot);

  const {
    printSettings: { addHeaders, addFooters },
  } = getPrinting(editable);

  const groupedContent = useMemo(() => {
    const { groups } = displayedVirtualRoot.subSections.reduce(
      groupConsistentContent,
      {
        groups: EMPTY_ARRAY,
        addHeaders,
        addFooters,
        supportPageCount: true,
        sectionTypesIdMap,
        generateId,
      },
    );

    if (groups.length) {
      const lastGroup: WritableDeep<PrintGroup> = groups[
        groups.length - 1
      ] as WritableDeep<PrintGroup>;

      if (lastGroup.sections.length) {
        const lastSection = lastGroup.sections[lastGroup.sections.length - 1];

        if (lastSection) {
          lastSection.hasPagebreakAfter = false;
        }
      }
    }

    return groups;
  }, [
    displayedVirtualRoot.subSections,
    addFooters,
    addHeaders,
    sectionTypesIdMap,
    generateId,
  ]);

  return groupedContent.length ? groupedContent : emptyDoc;
};
